/**
 * ERPs Store
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState, selectors } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { ERPsCommand } from './commands';
import {
  createERPsSyncPolicy,
  createERPsSubmitPolicy,
  createERPsDeletePolicy,
  createERPsDriftPolicy
} from './policies';

const store = createStore({
  name: 'erps',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useERPsSelector,
  useErrors: useERPsErrors,
  usePolicies: useERPsPolicies,
  getState: getERPsState,
  dispatch: dispatchERPsCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched.
 */
export const useERPsDispatch = (): Dispatch<ERPsCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<ERPsCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates policies that require backend access
 *
 * @param backend - Client library instance for API calls
 */
export const createERPsPolicies = (
  backend: ClientLibraryType
) => [
  createERPsSyncPolicy( backend ),
  createERPsSubmitPolicy( backend ),
  createERPsDeletePolicy( backend ),
  // TV1-2146 — the drift audit panel. Registered HERE, not in the tab: `ERPInfo`
  // is the single CDSE registration site (erpInfoTabs.tsx contract). The policy
  // gates itself on erpDetail.mode/activeTab and is inert otherwise.
  createERPsDriftPolicy( backend )
];

export { selectors };
